import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Row,
    Col,
    Nav,
    Tab,
} from 'react-bootstrap';
import { FiArrowLeft, FiUpload, FiCheckCircle } from 'react-icons/fi';
import {
    importTripsOlela,
    importTripsFlower,
    importTripsLacdesgraves,
    importTripsAloa,
    importTripsOdalys,
    getAllPartners,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { buildTripsPoliciesSpecificResultMessage } from '../../utils/tripsPoliciesImportResult';
import StandardImportPanel from '../../components/Admin/TripsPoliciesStandardImport/StandardImportPanel';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';
import './AdminTripsPoliciesImport.css';

const PAGE_PREFIX = 'pages.adminTripsPoliciesImport';
const STORAGE_KEY = 'importPoliciesMessage';

const IMPORT_MODES = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const SPECIFIC_PARTNER_OPTIONS = ['flower', 'odalys', 'aloa', 'lacdesgraves', 'olela'];

const SPECIFIC_IMPORT_FNS = {
    olela: { fn: importTripsOlela, typeNameKey: 'olela' },
    flower: { fn: importTripsFlower, typeNameKey: 'flower' },
    lacdesgraves: { fn: importTripsLacdesgraves, typeNameKey: 'lacdesgraves' },
    aloa: { fn: importTripsAloa, typeNameKey: 'aloa' },
    odalys: { fn: importTripsOdalys, typeNameKey: 'odalys' },
};

const AdminTripsPoliciesImport = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [importMode, setImportMode] = useState(IMPORT_MODES.STANDARD);
    const [specificPartner, setSpecificPartner] = useState('flower');
    const [importFile, setImportFile] = useState(null);
    const [importPartnerId, setImportPartnerId] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(() => {
        const savedMessage = localStorage.getItem(STORAGE_KEY);
        return savedMessage || null;
    });
    const [modalPartners, setModalPartners] = useState([]);
    const [modalPartnersLoading, setModalPartnersLoading] = useState(false);
    const [modalPartnersError, setModalPartnersError] = useState(null);

    const getResultLabels = () => ({
        importDone: t(`${PAGE_PREFIX}.specific.result.importDone`),
        successCount: t(`${PAGE_PREFIX}.standard.result.successCount`),
        errorCount: t(`${PAGE_PREFIX}.standard.result.errorCount`),
        createdPolicies: t(`${PAGE_PREFIX}.standard.result.createdPolicies`),
        skippedReservations: t(`${PAGE_PREFIX}.standard.result.skippedReservations`),
        skippedDefault: t(`${PAGE_PREFIX}.standard.result.skippedDefault`),
        errorsTitle: t(`${PAGE_PREFIX}.standard.result.errorsTitle`),
        unknownError: t(`${PAGE_PREFIX}.standard.result.unknownError`),
        reservation: t(`${PAGE_PREFIX}.specific.result.reservation`),
    });

    useEffect(() => {
        const loadPartners = async () => {
            setModalPartnersLoading(true);
            setModalPartnersError(null);
            try {
                const response = await getAllPartners();
                if (response?.users && Array.isArray(response.users)) {
                    setModalPartners(response.users);
                } else if (Array.isArray(response)) {
                    setModalPartners(response);
                } else {
                    setModalPartners([]);
                }
            } catch (error) {
                setModalPartnersError(handleApiError(error, t(`${PAGE_PREFIX}.specific.partnersError`)));
                setModalPartners([]);
            } finally {
                setModalPartnersLoading(false);
            }
        };
        loadPartners();
    }, [t]);

    const handleModeChange = (mode) => {
        if (!mode) return;
        setImportMode(mode);
        setImportFile(null);
        setImportError(null);
        setImportSuccess(null);
    };

    const handleSpecificPartnerChange = (partner) => {
        setSpecificPartner(partner);
        setImportFile(null);
        setImportPartnerId('');
        setImportError(null);
    };

    const persistSuccess = (message) => {
        setImportSuccess(message);
        localStorage.setItem(STORAGE_KEY, message);
    };

    const handleSpecificImport = async () => {
        if (!importFile) {
            setImportError(t(`${PAGE_PREFIX}.specific.validation.noFile`));
            return;
        }
        if (!importPartnerId) {
            setImportError(t(`${PAGE_PREFIX}.specific.validation.noPartner`));
            return;
        }

        const importConfig = SPECIFIC_IMPORT_FNS[specificPartner];
        if (!importConfig) {
            setImportError(t(`${PAGE_PREFIX}.specific.validation.invalidType`));
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            const response = await importConfig.fn(importPartnerId, importFile);
            const typeName = t(`${PAGE_PREFIX}.specificPartners.${importConfig.typeNameKey}`);
            const message = buildTripsPoliciesSpecificResultMessage(response, typeName, getResultLabels());
            persistSuccess(message);
            setImportFile(null);
            setImportPartnerId('');
        } catch (err) {
            setImportError(handleApiError(err, t(`${PAGE_PREFIX}.specific.error`)));
        } finally {
            setImportLoading(false);
        }
    };

    const handleResetSpecific = () => {
        setImportFile(null);
        setImportPartnerId('');
        setImportError(null);
        setImportSuccess(null);
        localStorage.removeItem(STORAGE_KEY);
    };

    const handleClearMessage = () => {
        setImportSuccess(null);
        localStorage.removeItem(STORAGE_KEY);
    };

    const renderSpecificForm = () => (
        <Form>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">
                            {t(`${PAGE_PREFIX}.specificPartnerLabel`)} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            value={specificPartner}
                            onChange={(e) => handleSpecificPartnerChange(e.target.value)}
                            disabled={importLoading}
                        >
                            {SPECIFIC_PARTNER_OPTIONS.map((partner) => (
                                <option key={partner} value={partner}>
                                    {t(`${PAGE_PREFIX}.specificPartners.${partner}`)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">
                            {t(`${PAGE_PREFIX}.specific.partnerLabel`)} <span className="text-danger">*</span>
                        </Form.Label>
                        {modalPartnersLoading ? (
                            <div className="d-flex align-items-center">
                                <Spinner size="sm" className="me-2" />
                                <span className="text-muted">{t(`${PAGE_PREFIX}.specific.partnersLoading`)}</span>
                            </div>
                        ) : (
                            <Form.Select
                                value={importPartnerId}
                                onChange={(e) => {
                                    setImportPartnerId(e.target.value);
                                    setImportError(null);
                                }}
                                disabled={importLoading || modalPartnersLoading}
                            >
                                <option value="">{t(`${PAGE_PREFIX}.specific.partnerPlaceholder`)}</option>
                                {modalPartners.map((partner) => {
                                    const partnerId = partner.user_uuid || partner.partner_id || partner.id;
                                    const partnerName = partner.company_name
                                        || `${partner.first_name || ''} ${partner.last_name || ''}`.trim()
                                        || partner.email
                                        || 'N/A';
                                    return (
                                        <option key={partnerId} value={partnerId}>
                                            {partnerName}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        )}
                        {modalPartnersError && (
                            <Alert variant="danger" className="mt-2 mb-0 py-2">
                                <small>{modalPartnersError}</small>
                            </Alert>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                    {t(`${PAGE_PREFIX}.specific.fileLabel`)} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            setImportFile(file);
                            setImportError(null);
                        }
                    }}
                    disabled={importLoading}
                />
                <Form.Text className="text-muted">{t(`${PAGE_PREFIX}.specific.fileHint`)}</Form.Text>
                {importFile && (
                    <div className="mt-2 small text-success">
                        <FiCheckCircle className="me-1" />
                        {t(`${PAGE_PREFIX}.specific.fileSelected`, { name: importFile.name })}
                    </div>
                )}
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-2">
                <Button variant="outline-secondary" onClick={handleResetSpecific} disabled={importLoading}>
                    {t('common.reset')}
                </Button>
                <Button
                    variant="success"
                    onClick={handleSpecificImport}
                    disabled={importLoading || !importPartnerId || !importFile}
                >
                    {importLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" animation="border" />
                            {t(`${PAGE_PREFIX}.specific.importing`)}
                        </>
                    ) : (
                        <>
                            <FiUpload className="me-2" />
                            {t(`${PAGE_PREFIX}.specific.importButton`)}
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );

    return (
        <div className="dashboard-page admin-trips-policies-page admin-trips-policies-import">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-4 import-page-header">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" aria-hidden />
                                {t(`${PAGE_PREFIX}.title`)}
                            </h2>
                            <p className="text-muted mb-0">
                                {importMode === IMPORT_MODES.STANDARD
                                    ? t(`${PAGE_PREFIX}.subtitleStandard`)
                                    : t(`${PAGE_PREFIX}.subtitle`)}
                            </p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)} className="d-flex align-items-center">
                            <FiArrowLeft className="me-2" aria-hidden />
                            {t('common.back')}
                        </Button>
                    </div>

                    <Tab.Container activeKey={importMode} onSelect={handleModeChange}>
                        <Nav variant="tabs" className="mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey={IMPORT_MODES.STANDARD}>
                                    {t(`${PAGE_PREFIX}.pageTabs.standard`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={IMPORT_MODES.SPECIFIC}>
                                    {t(`${PAGE_PREFIX}.pageTabs.specific`)}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={IMPORT_MODES.STANDARD}>
                                <StandardImportPanel t={t} />
                            </Tab.Pane>
                            <Tab.Pane eventKey={IMPORT_MODES.SPECIFIC}>
                                {importLoading && (
                                    <Alert variant="info" className="d-flex align-items-center gap-2 mb-4" role="status">
                                        <Spinner size="sm" animation="border" aria-hidden />
                                        <strong>{t(`${PAGE_PREFIX}.loading`)}</strong>
                                    </Alert>
                                )}
                                {importError && (
                                    <Alert variant="danger" dismissible onClose={() => setImportError(null)} className="mb-4" role="alert">
                                        {importError}
                                    </Alert>
                                )}
                                {renderSpecificForm()}
                                {importSuccess && (
                                    <Alert
                                        variant="success"
                                        className="import-result-alert d-flex align-items-start gap-2 mt-4"
                                        dismissible
                                        onClose={handleClearMessage}
                                        role="status"
                                    >
                                        <FiCheckCircle className="mt-1 flex-shrink-0" aria-hidden />
                                        <div className="flex-grow-1">
                                            <strong>{t(`${PAGE_PREFIX}.resultTitle`)}</strong>
                                            <pre className="import-result-pre">{importSuccess}</pre>
                                        </div>
                                    </Alert>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsPoliciesImport;
